import { useCallback, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import useSWR from 'swr'

import {
  menuSearchFetcher,
  type MenuSearchBasketRecipe,
  type MenuSearchInput,
  type MenuSearchResponse,
} from '@library/api-menu-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

type MenuExperienceSearchRequest = MenuSearchInput & {
  isOpen: boolean
}

export type MenuExperienceSearchSWRKey = {
  key: 'menu-experience-search'
  input: Omit<MenuSearchInput, 'recipes'>
}

type MenuExperienceSearchSWRResponse = {
  contextId: string
  query: string
  requestId: string
  response: MenuSearchResponse
}

const getMenuExperienceSearchRequestId = ({ input }: MenuExperienceSearchSWRKey): string =>
  JSON.stringify(input)

const getMenuExperienceSearchContextId = ({ input }: MenuExperienceSearchSWRKey): string =>
  JSON.stringify({ deliveryDate: input.deliveryDate, menuId: input.menuId })

export const getMenuExperienceSearchSWRKey = ({
  isOpen,
  menuId,
  query,
  numPortions,
  deliveryDate,
}: MenuExperienceSearchRequest): MenuExperienceSearchSWRKey | null =>
  isOpen && menuId && numPortions
    ? {
        key: 'menu-experience-search',
        input: {
          menuId,
          numPortions,
          ...(query && { query }),
          ...(deliveryDate && { deliveryDate }),
        },
      }
    : null

const fetchMenuExperienceSearch = async (
  key: MenuExperienceSearchSWRKey,
  recipes?: MenuSearchBasketRecipe[],
): Promise<MenuExperienceSearchSWRResponse> => {
  const input = {
    ...key.input,
    ...(recipes?.length && { recipes }),
  }
  try {
    const data = await menuSearchFetcher(input)

    return {
      contextId: getMenuExperienceSearchContextId(key),
      query: input.query ?? '',
      requestId: getMenuExperienceSearchRequestId(key),
      response: data,
    }
  } catch (error) {
    datadogRum.addError(error, {
      search_api: 'menu-experience',
      endpoint: `/menu/${input.menuId}/search`,
    })
    throw error
  }
}

export const useMenuExperienceSearch = (request: MenuExperienceSearchRequest) => {
  const key = getMenuExperienceSearchSWRKey(request)
  const currentContextId = key ? getMenuExperienceSearchContextId(key) : undefined
  const currentRequestId = key ? getMenuExperienceSearchRequestId(key) : undefined
  const currentRequestIdRef = useRef(currentRequestId)
  const queryTrackingRef = useRef({ query: request.query ?? '', hasTracked: false })
  const recipesRef = useRef(request.recipes)

  currentRequestIdRef.current = currentRequestId
  recipesRef.current = request.recipes
  if (queryTrackingRef.current.query !== (request.query ?? '')) {
    queryTrackingRef.current = { query: request.query ?? '', hasTracked: false }
  }

  const trackSuccessfulQuery = useCallback(
    (data: MenuExperienceSearchSWRResponse) => {
      const input = key?.input
      const { response } = data

      if (
        !input?.query ||
        response.type !== 'search-results' ||
        data.requestId !== currentRequestIdRef.current ||
        queryTrackingRef.current.query !== input.query ||
        queryTrackingRef.current.hasTracked
      ) {
        return
      }

      queueTrackingEvent<{
        action: 'search_query'
        properties: {
          menu_id: string
          query: string
          search_invocation_id: string
        }
      }>({
        action: 'search_query',
        properties: {
          menu_id: input.menuId,
          query: input.query,
          search_invocation_id: response.invocationId,
        },
      })
      datadogRum.addAction('submit_search', {
        menu_id: input.menuId,
        query: input.query,
        search_api: 'menu-experience',
        endpoint: `/menu/${input.menuId}/search`,
      })
      queryTrackingRef.current.hasTracked = true
    },
    [key],
  )
  const fetchCurrentMenuExperienceSearch = useCallback(
    (requestKey: MenuExperienceSearchSWRKey) =>
      fetchMenuExperienceSearch(requestKey, recipesRef.current),
    [],
  )
  const { data, error, isValidating, mutate } = useSWR(key, fetchCurrentMenuExperienceSearch, {
    keepPreviousData: true,
    onSuccess: trackSuccessfulQuery,
  })
  const isCurrentContext = key && data?.contextId === currentContextId
  const isCurrentResponse = key && data?.requestId === currentRequestId
  const isReplacingQueryResults = Boolean(
    isCurrentContext && data?.query !== (key?.input.query ?? '') && isValidating,
  )
  const retry = useCallback(() => {
    void mutate()
  }, [mutate])

  return {
    data: isCurrentContext ? data?.response : undefined,
    error: isCurrentResponse ? undefined : error,
    isLoading: !isCurrentContext && isValidating,
    isReplacingQueryResults,
    isValidating,
    retry,
  }
}

export const getMenuSearchBasketRecipes = (
  recipes: Array<{ id: MenuSearchBasketRecipe['id']; numPortions: number; quantity: number }>,
): MenuSearchBasketRecipe[] =>
  recipes.filter(({ quantity }) => quantity > 0).map(({ id, numPortions }) => ({ id, numPortions }))
